import { Empty, message, TimePicker, Input as AntInput } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';

import { ColumnsType } from 'antd/es/table';
import { Button, Card, Table, Text, Modal, Form, DatePicker } from '@bancobarigui/barihaus';

import { InspectionRequest, TimeSlot } from '@/services/types/inspection.types';
import { inspectionService } from '@/services/inspection.service';
import { ElementGuard } from '@/components/PermissionGuard';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import dayjs, { Dayjs } from 'dayjs';

interface InspectionSchedulesProps {
  inspection: InspectionRequest;
  onUpdate?: () => void;
}

interface ScheduleData {
  key: string;
  scheduleDate: string;
  timeSlot: string;
  notes: string;
  createdAt: string;
}

export function InspectionSchedules({ inspection, onUpdate }: InspectionSchedulesProps) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const firstItem = inspection.items?.[0];
  const itemSchedules = firstItem?.schedules || [];

  useEffect(() => {
    setIsModalVisible(false);
    form.resetFields();
  }, [form]);

  const handleNewSchedule = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSaveSchedule = async (values: any) => {
    if (!firstItem?.id) {
      alert('ID do item não encontrado');
      return;
    }

    setLoading(true);

    try {
      const startHour = values.startTime.hour();
      let timeSlot: TimeSlot;

      if (startHour < 12) {
        timeSlot = TimeSlot.Morning;
      } else if (startHour < 18) {
        timeSlot = TimeSlot.Afternoon;
      } else {
        timeSlot = TimeSlot.Evening;
      }

      const payload = {
        scheduleDate: values.date.format('YYYY-MM-DD'),
        timeSlot: timeSlot,
        notes: values.notes || '',
      };

      await inspectionService.scheduleInspectionItem(firstItem.id, payload);

      setIsModalVisible(false);
      form.resetFields();

      try {
        message.success('Agendamento criado com sucesso!');
      } catch {
        alert('Agendamento criado com sucesso!');
      }

      if (onUpdate) {
        setTimeout(() => onUpdate(), 100);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Erro ao criar agendamento. Tente novamente.';

      try {
        message.error(errorMessage);
      } catch {
        alert(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const schedules: ScheduleData[] = itemSchedules.map((schedule: any) => ({
    key: schedule.id,
    scheduleDate: schedule.scheduleDate,
    timeSlot: schedule.timeSlotDescription || '-',
    notes: schedule.notes || '-',
    createdAt: schedule.createdAt,
  }));

  const getWeekDay = (dateString: string) => {
    if (!dateString) return '-';
    const dateOnly = dateString.split('T')[0];
    if (!dateOnly) return '-';
    const parts = dateOnly.split('-');
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return '-';
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return '-';
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return '-';
    const days = [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado',
    ];
    return days[date.getDay()];
  };

  const getMonthYear = (dateString: string) => {
    if (!dateString) return '-';
    const dateOnly = dateString.split('T')[0];
    if (!dateOnly) return '-';
    const parts = dateOnly.split('-');
    if (parts.length !== 3 || !parts[0] || !parts[1]) return '-';
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (isNaN(year) || isNaN(month)) return '-';
    const months = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];
    return `${months[month - 1]}, ${year}`;
  };

  const getDay = (dateString: string) => {
    if (!dateString) return '-';
    const dateOnly = dateString.split('T')[0];
    if (!dateOnly) return '-';
    const parts = dateOnly.split('-');
    if (parts.length !== 3 || !parts[2]) return '-';
    const day = parseInt(parts[2], 10);
    if (isNaN(day)) return '-';
    return day.toString().padStart(2, '0');
  };

  const formatTimeSlot = (timeSlotDescription: string) => {
    if (!timeSlotDescription) return '-';

    const timeSlotMap: { [key: string]: string } = {
      'Morning': '08:00 - 12:00',
      'Afternoon': '12:00 - 18:00',
      'Evening': '18:00 - 22:00',
      'Manhã': '08:00 - 12:00',
      'Tarde': '12:00 - 18:00',
      'Noite': '18:00 - 22:00',
    };

    return timeSlotMap[timeSlotDescription] || timeSlotDescription;
  };

  const columns: ColumnsType<ScheduleData> = [
    {
      title: 'DATA AGENDAMENTO',
      dataIndex: 'scheduleDate',
      key: 'scheduleDate',
      render: (date: string) => (
        <div className="flex items-center gap-4">
          <div className="w-1 bg-green-500 h-10 rounded-full" />
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-gray-800">{getDay(date)}</span>
            <div>
              <div className="text-sm font-medium text-gray-700">{getWeekDay(date)}</div>
              <div className="text-sm text-gray-500">{getMonthYear(date)}</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'HORÁRIO',
      dataIndex: 'timeSlot',
      key: 'timeSlot',
      render: (timeSlot: string) => <Text className="text-sm text-gray-700">{formatTimeSlot(timeSlot)}</Text>,
    },
    {
      title: 'OBSERVAÇÃO',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => <Text className="text-sm text-gray-700">{notes || '-'}</Text>,
    },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto w-full">
      <div className="flex justify-center">
        <ElementGuard permissions={[UserPermissions.inspections_schedule_bank, UserPermissions.inspections_schedule_provider, UserPermissions.inspections_schedule_inspector]}>
          <Button
            icon={<CalendarOutlined />}
            onClick={handleNewSchedule}
            className="!border-0"
            style={{ backgroundColor: '#0090ff', color: '#fff', boxShadow: 'none' }}
          >
            Novo Agendamento
          </Button>
        </ElementGuard>
      </div>

      {(!schedules || schedules.length === 0) ? (
        <div className="flex items-center justify-center py-20">
          <Empty
            description={
              <div className="space-y-2">
                <Text className="text-gray-500 block">Nenhum agendamento registrado</Text>
                <Text className="text-gray-400 text-sm">
                  Os agendamentos aparecerão aqui quando a inspeção for agendada
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <Card className="shadow-sm">
          <div className="overflow-x-auto">
            <Table
              dataSource={schedules}
              columns={columns as any}
              pagination={false}
              rowKey="key"
              locale={{
                emptyText: <Text className="text-gray-500">Nenhum agendamento encontrado.</Text>,
              }}
            />
          </div>
        </Card>
      )}

      <Modal
        title="Novo Agendamento"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        zIndex={10001}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSaveSchedule} className="mt-4">
          <Form.Item
            label="Data do Agendamento"
            name="date"
            rules={[{ required: true, message: 'Data é obrigatória' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Selecione a data"
              disabledDate={(current: Dayjs) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item
            label="Horário de Início"
            name="startTime"
            rules={[{ required: true, message: 'Horário de início é obrigatório' }]}
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm" placeholder="00:00" />
          </Form.Item>

          <Form.Item label="Observações" name="notes">
            <AntInput.TextArea rows={3} placeholder="Observações sobre o agendamento (opcional)" />
          </Form.Item>

          <div className="flex justify-center gap-3 mt-6">
            <Button
              onClick={handleCancel}
              style={{ borderColor: '#101010', color: '#101010', backgroundColor: 'transparent', boxShadow: 'none' }}
            >
              Cancelar
            </Button>
            <Button
              htmlType="submit"
              loading={loading}
              className="!border-0"
              style={{ backgroundColor: '#0090ff', color: '#fff', boxShadow: 'none' }}
            >
              Criar Agendamento
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
